import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { ApplicationState } from '../../../reducers';
import { APP_CONFIG } from '../../tokens/app-config.token';
import { Environment } from '../../../../environments/environment.interface';
import { getUserIdSelector } from '../../ngrx/state/user.state';
import { GtmEvents, GtmCategories } from '../../enums/analytics.enum';
import { GtmMetaData, GtmFlowEvent } from '../../model/analytic-event.model';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  userId = null;

  constructor(
    private readonly store: Store<ApplicationState>,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(APP_CONFIG) private readonly appConfig: Environment,
  ) {
    this.store
      .select(getUserIdSelector)
      .pipe()
      .subscribe((userId) => {
        this.userId = userId;
      });
  }

  /* This method initiate Google Tag Manager */
  initiateGoogleTagManager() {
    if (isPlatformBrowser(this.platformId)) {
      ((w, d, s, l, i) => {
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        const f = d.getElementsByTagName(s)[0];
        const j: HTMLScriptElement = d.createElement('script');
        const dl = l != 'dataLayer' ? `&l=${l}` : '';
        j.async = true;
        j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', this.appConfig.GOOGLE_TAG_MANAGER_ID);
    }
  }

  /* This method sends Google Tag Manager page event */
  sendGtmPageEvent(userId) {
    if (isPlatformBrowser(this.platformId)) {
      const gtmPageTag: GtmMetaData = {
        event: 'GTM page to GA',
        pageURL: window.location.href,
        pageSection: this.appConfig.GOOGLE_ANALYTIC_SECTION,
        userID: userId || null,
        isLoggedIn: userId ? 'yes' : 'no',
      };

      this.sendGtmEvent(gtmPageTag);
    }
  }

  /* This method sends Google Tag Manager SIGNUP event */
  sendGtmSignupEvent(action, label, content?, userId?, initiator?, submitEmail?, submissionId?, method?) {
    const gtmSignupEvent: GtmFlowEvent = {
      event: GtmEvents.GTM_EVENT_TO_GA,
      GA_event_category: GtmCategories.SIGNUP,
      GA_event_action: action,
      GA_event_label: label,
      GA_event_method: method || null,
      content: content || 'na',
      userID: userId || null,
      initiator,
      submitEmail,
      submissionId,
    };
    this.sendGtmEvent(gtmSignupEvent);
  }

  /* This method sends Google Tag Manager LOGIN event */
  sendGtmLoginEvent(action, label, content?, userId?, initiator?, method?) {
    const gtmSignupEvent: GtmFlowEvent = {
      event: GtmEvents.GTM_EVENT_TO_GA,
      GA_event_category: GtmCategories.LOGIN,
      GA_event_action: action,
      GA_event_label: label,
      GA_event_method: method || null,
      content: content || 'na',
      userID: userId || null,
      initiator,
    };

    this.sendGtmEvent(gtmSignupEvent);
  }

  /* This method sends Google Tag Manager SIGNUP event */
  sendSignUpFormValueChangeEvent(action, label, content) {
    const signUpFormValueChange = {
      event: GtmEvents.GTM_EVENT_TO_GA,
      GA_event_category: GtmCategories.SIGNUP,
      GA_event_action: action,
      GA_event_label: label,
      content: content || 'na',
    };

    this.sendGtmEvent(signUpFormValueChange);
  }

  /* This method sends Google Tag Manager COUPON event */
  sendGtmCouponEvent(action, label, content?, initiator?) {
    const gtmCouponEvent: GtmFlowEvent = {
      event: GtmEvents.GTM_EVENT_TO_GA,
      GA_event_category: GtmCategories.ECOMMERCE,
      GA_event_action: action,
      GA_event_label: label,
      content: content || 'na',
      userID: this.userId,
      initiator,
    };

    this.sendGtmEvent(gtmCouponEvent);
  }

  /* This method sends Google Tag Manager INSTALL-EXT event */
  sendGtmInstallExtEvent(action, label, content?, initiator?) {
    const gtmCouponEvent: GtmFlowEvent = {
      event: GtmEvents.GTM_EVENT_TO_GA,
      GA_event_category: GtmCategories.SIGNUP,
      GA_event_action: action,
      GA_event_label: label,
      content: content || 'na',
      userID: this.userId,
      initiator,
    };

    this.sendGtmEvent(gtmCouponEvent);
  }

  /* This method sends Google Tag Manager VIEW ACCOUNT event */
  sendGtmViewAccountEvent(action, label, content?, initiator?) {
    const gtmCouponEvent: GtmFlowEvent = {
      event: GtmEvents.GTM_EVENT_TO_GA,
      GA_event_category: GtmCategories.SIGNUP,
      GA_event_action: action,
      GA_event_label: label,
      content: content || 'na',
      userID: this.userId,
      initiator,
    };

    this.sendGtmEvent(gtmCouponEvent);
  }

  /* This method is responsible to send Google Analytics events */
  sendGtmEvent(event) {
    if (isPlatformBrowser(this.platformId)) {
      window['dataLayer'] = window['dataLayer'] || [];
      window['dataLayer'].push(event);
    }
  }

  fixGAClientId(id: string) {
    return id ? id.substring(id.indexOf('GA1.2') + 6) : '';
  }
}
